/**
 * TeamSidebarSection: a team's sidebar visibility and landing page
 * (TRA-927), set by staff. A checkbox per nav entry (checked = visible)
 * and a landing-page picker limited to the visible entries. The API
 * re-validates against KNOWN_NAV_ROUTES and rejects a hidden landing
 * page; a 400 shows in the form footer. An entry that carries Settings
 * sections (sections.ts `navRoute`) says so, since unchecking it hides
 * those too.
 *
 * The saved values are the team prop; the form holds the edits. A save
 * writes the API's answer into the cached team list, so the prop catches
 * up at once and the form reads clean. The staff member may belong to
 * the team they just configured, and their own sidebar reads /me, so
 * that refreshes too.
 */

import { useState, type FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { customFetch, getApiErrorDetail, getApiErrorStatus } from '@/api/client'
import type {
  ImpersonatableTeam,
  TeamListResponse,
  UpdateTeamNavRequest,
} from '@/api/generated/model'
import { settingsSectionsForNavRoute } from '@/components/settings/sections'
import { SettingsFormFooter } from '@/components/settings/SettingsFormFooter'
import { SettingsPageHeader } from '@/components/settings/SettingsPageHeader'
import { SettingsSection } from '@/components/settings/SettingsSection'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { INTERNAL_TEAMS_QUERY_KEY } from '@/hooks/useInternalTeams'
import { NAV_ENTRIES, type NavEntry } from '@/lib/nav'

import type { TeamConfigSectionProps } from './sections'

const LANDING_HIDDEN_ERROR = 'The landing page cannot be hidden. Pick a visible landing page first.'

export function TeamSidebarSection({ section, team }: TeamConfigSectionProps): React.JSX.Element {
  const queryClient = useQueryClient()
  const [hidden, setHidden] = useState<string[]>(team.hidden_nav_items)
  const [landing, setLanding] = useState(team.landing_page)
  // The API's answer to the last save. Any edit retires it: the next save
  // is a new question.
  const [saveError, setSaveError] = useState<string | null>(null)

  const saveMutation = useMutation({
    mutationFn: (body: UpdateTeamNavRequest) =>
      customFetch<ImpersonatableTeam>(`/api/internal/teams/${team.id}/nav`, {
        method: 'PUT',
        data: body,
      }),
    onSuccess: (saved) => {
      queryClient.setQueryData<TeamListResponse>(INTERNAL_TEAMS_QUERY_KEY, (previous) =>
        previous === undefined
          ? previous
          : { teams: previous.teams.map((row) => (row.id === saved.id ? saved : row)) },
      )
      void queryClient.invalidateQueries({ queryKey: ['/api/auth/me'] })
      toast.success('Sidebar saved')
    },
    onError: (error: unknown) => {
      const detail = getApiErrorDetail(error) ?? 'Failed to save the sidebar'
      if (getApiErrorStatus(error) === 400) {
        setSaveError(detail)
      } else {
        toast.error(detail)
      }
    },
  })

  const dirty = !sameRoutes(hidden, team.hidden_nav_items) || landing !== team.landing_page
  const landingHidden = hidden.includes(landing)
  const canSubmit = !saveMutation.isPending && dirty && !landingHidden

  const toggleRoute = (to: string, visible: boolean): void => {
    setHidden((previous) => (visible ? previous.filter((route) => route !== to) : [...previous, to]))
    setSaveError(null)
  }
  const discard = (): void => {
    setHidden(team.hidden_nav_items)
    setLanding(team.landing_page)
    setSaveError(null)
  }
  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault()
    if (!canSubmit) return
    setSaveError(null)
    saveMutation.mutate({ hidden_nav_items: hidden, landing_page: landing })
  }

  return (
    <div>
      <SettingsPageHeader section={section} />
      <form onSubmit={handleSubmit}>
        <SettingsSection
          title="Visible pages"
          description="An unchecked page leaves the sidebar. A page that carries Settings sections takes them with it."
        >
          <fieldset className="space-y-3">
            <legend className="sr-only">Visible pages</legend>
            {NAV_ENTRIES.map((entry) => (
              <NavEntryCheckbox
                key={entry.to}
                entry={entry}
                checked={!hidden.includes(entry.to)}
                onCheckedChange={(visible) => {
                  toggleRoute(entry.to, visible)
                }}
              />
            ))}
          </fieldset>
        </SettingsSection>

        <SettingsSection
          title="After sign-in"
          description="The team opens this page after sign-in. It has to be a visible page."
        >
          <div className="max-w-xs space-y-2">
            <Label htmlFor="team-landing-page">Landing page</Label>
            <Select
              value={landing}
              onValueChange={(value) => {
                setLanding(value)
                setSaveError(null)
              }}
            >
              <SelectTrigger id="team-landing-page">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {NAV_ENTRIES.filter((entry) => !hidden.includes(entry.to)).map((entry) => (
                  <SelectItem key={entry.to} value={entry.to}>
                    {entry.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </SettingsSection>

        <SettingsFormFooter dirty={dirty} error={landingHidden ? LANDING_HIDDEN_ERROR : saveError}>
          <Button
            type="button"
            variant="outline"
            disabled={!dirty || saveMutation.isPending}
            onClick={discard}
          >
            Discard
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            Save
          </Button>
        </SettingsFormFooter>
      </form>
    </div>
  )
}

/** Order-insensitive: the API stores a set of routes. */
function sameRoutes(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((route) => b.includes(route))
}

function NavEntryCheckbox({
  entry,
  checked,
  onCheckedChange,
}: {
  entry: NavEntry
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}): React.JSX.Element {
  const id = `team-nav-${entry.to}`
  return (
    <div className="flex items-start gap-2">
      <Checkbox
        id={id}
        className="mt-0.5"
        checked={checked}
        onCheckedChange={(state) => {
          onCheckedChange(state === true)
        }}
      />
      <Label htmlFor={id} className="font-normal">
        {entry.label}
        <span className="ml-1.5 font-mono text-xs text-muted-foreground">{entry.to}</span>
        <SettingsCarriedByRoute to={entry.to} />
      </Label>
    </div>
  )
}

/** "Also hides Tags, Review scans and Prompts in Settings." under an entry. */
function SettingsCarriedByRoute({ to }: { to: string }): React.JSX.Element | null {
  const labels = settingsSectionsForNavRoute(to).map((section) => section.label)
  if (labels.length === 0) return null
  const head = labels.slice(0, -1).join(', ')
  const tail = labels.slice(-1).join('')
  const list = head === '' ? tail : `${head} and ${tail}`
  return <span className="block text-xs text-muted-foreground">Also hides {list} in Settings.</span>
}
